package top.wcpe.allincore.layout.engine

import kotlin.jvm.JvmField
import kotlin.jvm.JvmStatic

/**
 * 单节点布局样式。
 * 公共字段 + 流式 setter，便于直接组合使用。
 *
 * ADR-0034：保留「public 字段 + 同名流式 setter」双形态（JVM 端 `@JvmField` 保字段直读直写），
 * 下游两种用法零改动。
 */
class LayoutStyle private constructor() {

    @JvmField
    var mode: LayoutMode = LayoutMode.ROW

    @JvmField
    var width: Dimension = Dimension.auto()

    @JvmField
    var height: Dimension = Dimension.auto()

    @JvmField
    var minWidth: Dimension = Dimension.auto()

    @JvmField
    var minHeight: Dimension = Dimension.auto()

    @JvmField
    var maxWidth: Dimension = Dimension.auto()

    @JvmField
    var maxHeight: Dimension = Dimension.auto()

    @JvmField
    var margin: Edges = Edges.zero()

    @JvmField
    var padding: Edges = Edges.zero()

    /** 主轴与交叉轴间距（统一一个值，简化版）。 */
    @JvmField
    var gap: Float = 0f

    @JvmField
    var align: Align = Align.START

    @JvmField
    var justify: Justify = Justify.START

    @JvmField
    var flexGrow: Float = 0f

    @JvmField
    var flexShrink: Float = 1f

    @JvmField
    var flexBasis: Dimension = Dimension.auto()

    /** 定位策略；[PositionMode.ABSOLUTE] 时 [x]/[y] 生效，且不占父容器流式布局空间。 */
    @JvmField
    var positionMode: PositionMode = PositionMode.RELATIVE

    /** 绝对定位下相对最近 positioned 祖先的 X 偏移；默认 `auto`。 */
    @JvmField
    var x: Dimension = Dimension.auto()

    /** 绝对定位下相对最近 positioned 祖先的 Y 偏移；默认 `auto`。 */
    @JvmField
    var y: Dimension = Dimension.auto()

    /** Flex 换行模式。默认不换行。 */
    @JvmField
    var flexWrap: FlexWrap = FlexWrap.NOWRAP

    /** Grid 列数（简化 API：等宽 N 列，等价于 repeat(N, 1fr)）。0 表示不使用 grid。 */
    @JvmField
    var gridColumns: Int = 0

    /** Grid 行数（0 = auto，由子项数量自动决定行数）。 */
    @JvmField
    var gridRows: Int = 0

    /**
     * Grid 列 track-list 原始字符串（CSS grid-template-columns 文法，如 `"1fr 2fr 100px"`）。
     * `null` / 空串 = 落回旧 [gridColumns] 等宽路径（零回归）。
     */
    @JvmField
    var gridTemplateColumns: String? = null

    /**
     * Grid 行 track-list 原始字符串（CSS grid-template-rows 文法）。
     * `null` / 空串 = 落回旧 [gridRows] 等宽路径（零回归）。
     */
    @JvmField
    var gridTemplateRows: String? = null

    /**
     * Grid 子项列放置原始字符串（CSS grid-column 文法，如 `"span 2"` / `"2"` / `"1 / 3"`）。
     * `null` = auto（Taffy 默认自动放置）。这是子项属性，写在子元素上。
     */
    @JvmField
    var gridColumn: String? = null

    /**
     * Grid 子项行放置原始字符串（CSS grid-row 文法）。
     * `null` = auto（Taffy 默认自动放置）。这是子项属性，写在子元素上。
     */
    @JvmField
    var gridRow: String? = null

    /**
     * Grid 自动流向：`"row"` / `"column"` / `"row-dense"` / `"column-dense"`。
     * `null` = Taffy 默认 ROW。
     */
    @JvmField
    var gridAutoFlow: String? = null

    /**
     * Grid 子项主轴对齐（复用 [Align] 枚举）。
     * `null` = Taffy 默认（STRETCH 语义）。
     */
    @JvmField
    var justifyItems: Align? = null

    /**
     * 宽高比约束（宽 / 高）。[Float.NaN] = 不约束（与 Taffy ctor 默认一致，零回归）。
     * 对任意元素生效（不限 grid）：一维 definite、另一维 auto 时据此推导另一维。
     */
    @JvmField
    var aspectRatio: Float = Float.NaN

    fun mode(v: LayoutMode): LayoutStyle { this.mode = v; return this }

    fun width(v: Dimension): LayoutStyle { this.width = v; return this }
    fun height(v: Dimension): LayoutStyle { this.height = v; return this }
    fun minWidth(v: Dimension): LayoutStyle { this.minWidth = v; return this }
    fun minHeight(v: Dimension): LayoutStyle { this.minHeight = v; return this }
    fun maxWidth(v: Dimension): LayoutStyle { this.maxWidth = v; return this }
    fun maxHeight(v: Dimension): LayoutStyle { this.maxHeight = v; return this }

    fun margin(v: Edges): LayoutStyle { this.margin = v; return this }
    fun padding(v: Edges): LayoutStyle { this.padding = v; return this }
    fun gap(v: Float): LayoutStyle { this.gap = v; return this }

    fun align(v: Align): LayoutStyle { this.align = v; return this }
    fun justify(v: Justify): LayoutStyle { this.justify = v; return this }

    fun flexGrow(v: Float): LayoutStyle { this.flexGrow = v; return this }
    fun flexShrink(v: Float): LayoutStyle { this.flexShrink = v; return this }
    fun flexBasis(v: Dimension): LayoutStyle { this.flexBasis = v; return this }

    fun positionMode(v: PositionMode): LayoutStyle { this.positionMode = v; return this }
    fun x(v: Dimension): LayoutStyle { this.x = v; return this }
    fun y(v: Dimension): LayoutStyle { this.y = v; return this }
    fun flexWrap(v: FlexWrap): LayoutStyle { this.flexWrap = v; return this }
    fun gridColumns(v: Int): LayoutStyle { this.gridColumns = v; return this }
    fun gridRows(v: Int): LayoutStyle { this.gridRows = v; return this }

    fun gridTemplateColumns(v: String?): LayoutStyle { this.gridTemplateColumns = v; return this }
    fun gridTemplateRows(v: String?): LayoutStyle { this.gridTemplateRows = v; return this }
    fun gridColumn(v: String?): LayoutStyle { this.gridColumn = v; return this }
    fun gridRow(v: String?): LayoutStyle { this.gridRow = v; return this }
    fun gridAutoFlow(v: String?): LayoutStyle { this.gridAutoFlow = v; return this }
    fun justifyItems(v: Align?): LayoutStyle { this.justifyItems = v; return this }
    fun aspectRatio(v: Float): LayoutStyle { this.aspectRatio = v; return this }

    /**
     * 基于全部布局字段的内容相等性（FR-30 B1：供增量布局指纹 memo 比较）。
     *
     * 嵌套值类型 [Dimension] / [Edges] 已自带内容 equals；float 用逐位比较
     * （等价 Java `Float.compare`）以使 `aspectRatio` 默认值 `NaN` 自反相等。
     */
    override fun equals(other: Any?): Boolean {
        if (this === other) {
            return true
        }
        if (other !is LayoutStyle) {
            return false
        }
        return gap.toBits() == other.gap.toBits() &&
            flexGrow.toBits() == other.flexGrow.toBits() &&
            flexShrink.toBits() == other.flexShrink.toBits() &&
            aspectRatio.toBits() == other.aspectRatio.toBits() &&
            gridColumns == other.gridColumns &&
            gridRows == other.gridRows &&
            mode == other.mode &&
            width == other.width &&
            height == other.height &&
            minWidth == other.minWidth &&
            minHeight == other.minHeight &&
            maxWidth == other.maxWidth &&
            maxHeight == other.maxHeight &&
            margin == other.margin &&
            padding == other.padding &&
            align == other.align &&
            justify == other.justify &&
            flexBasis == other.flexBasis &&
            positionMode == other.positionMode &&
            x == other.x &&
            y == other.y &&
            flexWrap == other.flexWrap &&
            gridTemplateColumns == other.gridTemplateColumns &&
            gridTemplateRows == other.gridTemplateRows &&
            gridColumn == other.gridColumn &&
            gridRow == other.gridRow &&
            gridAutoFlow == other.gridAutoFlow &&
            justifyItems == other.justifyItems
    }

    override fun hashCode(): Int {
        // 逐位复刻原 Java Objects.hash(...) 的 31 链，元素顺序一致；null 记 0
        var result = 1
        result = 31 * result + mode.hashCode()
        result = 31 * result + width.hashCode()
        result = 31 * result + height.hashCode()
        result = 31 * result + minWidth.hashCode()
        result = 31 * result + minHeight.hashCode()
        result = 31 * result + maxWidth.hashCode()
        result = 31 * result + maxHeight.hashCode()
        result = 31 * result + margin.hashCode()
        result = 31 * result + padding.hashCode()
        result = 31 * result + gap.hashCode()
        result = 31 * result + align.hashCode()
        result = 31 * result + justify.hashCode()
        result = 31 * result + flexGrow.hashCode()
        result = 31 * result + flexShrink.hashCode()
        result = 31 * result + flexBasis.hashCode()
        result = 31 * result + positionMode.hashCode()
        result = 31 * result + x.hashCode()
        result = 31 * result + y.hashCode()
        result = 31 * result + flexWrap.hashCode()
        result = 31 * result + gridColumns.hashCode()
        result = 31 * result + gridRows.hashCode()
        result = 31 * result + (gridTemplateColumns?.hashCode() ?: 0)
        result = 31 * result + (gridTemplateRows?.hashCode() ?: 0)
        result = 31 * result + (gridColumn?.hashCode() ?: 0)
        result = 31 * result + (gridRow?.hashCode() ?: 0)
        result = 31 * result + (gridAutoFlow?.hashCode() ?: 0)
        result = 31 * result + (justifyItems?.hashCode() ?: 0)
        result = 31 * result + aspectRatio.hashCode()
        return result
    }

    companion object {

        @JvmStatic
        fun create(): LayoutStyle {
            return LayoutStyle()
        }
    }
}
